/**
 * What the hints and tips report (trial telemetry, docs/architecture/telemetry-catalog.md): each hint shown (once per
 * session), closed with its X or retired by its own rule, and its button used; the Show hints checkbox turned on or
 * off; and each setup-screen tip shown and opened. Names are the hint and tip ids (document-hints.ts, setup-tips.ts),
 * never their words or counts. The job's id goes to main, which sends only the job's kind.
 *
 * The rules live here, apart from the screens, so they can be tested alone (tests/telemetry-tests/tour-hints.cjs).
 */
import { reportEvent } from './telemetry';
import { telemetryTips, type TelemetryTip } from '../shared/telemetry-catalog';
import type { HintId } from './document-hints';
import type { TelemetryDeps } from './tour-telemetry';

export function createHintTelemetry(deps: Pick<TelemetryDeps, 'reportEvent'>) {
  const shown = new Set<HintId>();
  let speedTestRetired = false;
  return {
    /** A hint on screen: once per session per hint. */
    shown(id: HintId, jobId?: string): void {
      if (shown.has(id)) return;
      shown.add(id);
      deps.reportEvent(`hint.shown:${id}`, { jobId });
    },

    /**
     * Closed with its X, or retired by its own rule once it has done its job. A rule retiring a hint that wasn't shown
     * this session says nothing about the hint, so it isn't sent.
     */
    closed(id: HintId, how: 'x' | 'rule', jobId?: string): void {
      if (how === 'rule' && !shown.has(id)) return;
      deps.reportEvent(`hint.closed:${id}`, { jobId, attrs: { how } });
    },

    /** A speed test finished on this computer: the speed test hint, if it was shown and not closed, retires by its rule (once). */
    speedTestFinished(closedByX: boolean, jobId?: string): void {
      if (speedTestRetired || closedByX || !shown.has('computer-check')) return;
      speedTestRetired = true;
      deps.reportEvent('hint.closed:computer-check', { jobId, attrs: { how: 'rule' } });
    },

    /** The hint's own button: Run speed test, or Open Pilot mode. */
    action(id: HintId, action: 'speed-test' | 'pilot-mode', jobId?: string): void {
      deps.reportEvent(`hint.action:${id}`, { jobId, attrs: { action } });
    },

    /** Settings › Appearance › Show hints, checked or cleared. */
    showHints(on: boolean, from: 'settings' | 'hint'): void {
      deps.reportEvent('setting.changed:show-hints', { attrs: { kind: on ? 'on' : 'off', from } });
    },
  };
}

export type HintTelemetry = ReturnType<typeof createHintTelemetry>;

/** This window's hint telemetry. */
export const hintTelemetry: HintTelemetry = createHintTelemetry({ reportEvent });

const isTip = (id: string): id is TelemetryTip => (telemetryTips as readonly string[]).includes(id);

/**
 * The setup screen's tips (setup-tips.ts): each tip shown, once per session, and each opened with Learn more. A tip
 * the catalog doesn't list yet is not sent.
 */
export function createTipTelemetry(deps: Pick<TelemetryDeps, 'reportEvent'>) {
  const shown = new Set<string>();
  return {
    shown(id: string, jobId?: string): void {
      if (!isTip(id) || shown.has(id)) return;
      shown.add(id);
      deps.reportEvent(`tip.shown:${id}`, { jobId });
    },
    opened(id: string, jobId?: string): void {
      if (isTip(id)) deps.reportEvent(`tip.opened:${id}`, { jobId });
    },
  };
}

/** This window's tip telemetry. */
export const tipTelemetry = createTipTelemetry({ reportEvent });
